import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './context/AuthContext';
import { getDomainConfig } from './config/domainConfig';

// PWA Install Prompt
function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [showInstall, setShowInstall] = React.useState(false);

  React.useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setShowInstall(false);
    setDeferredPrompt(null);
  };

  return { showInstall, install, setShowInstall };
}
import Login from './pages/Login';
import CustomerPortal from './pages/CustomerPortal';
import Dashboard from './pages/Dashboard';
import Devices from './pages/Devices';
import Users from './pages/Users';
import Tenants from './pages/Tenants';
import Hierarchy from './pages/Hierarchy';
import Stock from './pages/Stock';

const App = () => {
  const { user, loading, logout } = useAuth();
  const domainConfig = getDomainConfig();
  const primary = domainConfig.primary_color || '#1B6B3A';
  document.title = (domainConfig.app_name || 'My Connection') + ' | Network Portal';
  const { showInstall, install, setShowInstall } = usePWAInstall();

  // Customer portal
  if (domainConfig.type === 'customer') {
    return (
      <>
        {showInstall && (
          <div style={{position:'fixed',bottom:'16px',left:'50%',transform:'translateX(-50%)',zIndex:9999,background:'#1B6B3A',color:'white',padding:'12px 20px',borderRadius:'12px',display:'flex',alignItems:'center',gap:'12px',boxShadow:'0 4px 20px rgba(0,0,0,0.3)',maxWidth:'340px',width:'90%'}}>
            <span style={{fontSize:'24px'}}>📱</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:'700',fontSize:'14px'}}>Install App</div>
              <div style={{fontSize:'12px',opacity:0.9}}>Add to home screen for quick access</div>
            </div>
            <button onClick={install} style={{background:'white',color:'#1B6B3A',border:'none',borderRadius:'8px',padding:'6px 12px',fontWeight:'700',cursor:'pointer',fontSize:'13px'}}>Install</button>
            <button onClick={()=>setShowInstall(false)} style={{background:'none',border:'none',color:'white',cursor:'pointer',fontSize:'18px',padding:'0 4px'}}>✕</button>
          </div>
        )}
        <CustomerPortal primary={primary} domainConfig={domainConfig} />
      </>
    );
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: '48px', height: '48px', border: `3px solid ${primary}22`, borderTop: `3px solid ${primary}`, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        <div style={{ color: '#ffffff44', fontSize: '14px' }}>Loading...</div>
      </div>
    </div>
  );

  if (!user) return <Login domainConfig={domainConfig} />;

  return <Layout user={user} logout={logout} primary={primary} domainConfig={domainConfig} />;
};

const Layout = ({ user, logout, primary, domainConfig }) => {
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdMsg, setPwdMsg] = useState({ text: '', type: '' });
  const [pwdLoading, setPwdLoading] = useState(false);

  const handleChangePwd = async () => {
    if (pwdForm.newPwd !== pwdForm.confirm) { setPwdMsg({ text: 'Passwords do not match', type: 'error' }); return; }
    if (pwdForm.newPwd.length < 6) { setPwdMsg({ text: 'Min 6 characters required', type: 'error' }); return; }
    try {
      setPwdLoading(true);
      await axios.post('/api/auth/change-password', {
        tenant_code: user.tenant?.code,
        email: user.lco_id || user.email,
        current_password: pwdForm.current,
        new_password: pwdForm.newPwd
      });
      setPwdMsg({ text: 'Password changed!', type: 'success' });
      setTimeout(() => { setShowChangePwd(false); setPwdForm({ current: '', newPwd: '', confirm: '' }); setPwdMsg({ text: '', type: '' }); }, 2000);
    } catch(e) { setPwdMsg({ text: e.response?.data?.message || 'Failed', type: 'error' }); }
    finally { setPwdLoading(false); }
  };
  const [page, setPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const isSuperAdmin = user.role === 'superadmin';
  const isDirector = user.role === 'director';

  useEffect(() => {
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchPendingCount = async () => {
    try {
      const res = await axios.get('/api/acs/unregistered');
      setPendingCount(res.data.count || 0);
    } catch (e) {}
  };

  const menuItems = [
    { id: 'dashboard', icon: '⚡', label: 'Dashboard' },
    { id: 'devices', icon: '📡', label: 'Devices', badge: pendingCount > 0 ? pendingCount : null },
    ...(!isDirector ? [{ id: 'users', icon: '👥', label: 'Users' }] : []),
    ...(isSuperAdmin ? [{ id: 'tenants', icon: '🏢', label: 'Tenants' }] : []),
    ...(!isSuperAdmin && !isDirector ? [{ id: 'hierarchy', icon: '🌐', label: 'Network Hierarchy' }] : []),
    ...(!isDirector ? [{ id: 'stock', icon: '📦', label: 'ONT Stock' }] : []),
    
  ];

  const pages = { dashboard: Dashboard, devices: Devices, users: Users, tenants: Tenants, hierarchy: Hierarchy, stock: Stock };
  const PageComponent = pages[page] || Dashboard;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f1f5f9', fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&family=DM+Mono&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
      `}</style>

      {/* Sidebar */}
      <div style={{
        width: sidebarOpen ? '240px' : '68px',
        background: domainConfig.logo_url ? 'white' : '#0f172a',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', top: 0, left: 0, bottom: 0,
        zIndex: 100, transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
        boxShadow: domainConfig.logo_url ? '1px 0 0 #e2e8f0' : '4px 0 24px rgba(0,0,0,0.15)', borderRight: domainConfig.logo_url ? '1px solid #e2e8f0' : 'none'
      }}>
        {/* Logo */}
        <div style={{ background: domainConfig.logo_url ? 'white' : 'transparent', padding: domainConfig.logo_url ? '12px 16px' : '20px 16px', borderBottom: domainConfig.logo_url ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.06)', borderRight: domainConfig.logo_url ? '1px solid #e2e8f0' : 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', minHeight: domainConfig.logo_url ? '100px' : '68px' }}>
          {domainConfig.logo_url ? (
            <img src={domainConfig.logo_url} alt='logo' style={{ width: '160px', height: '65px', objectFit: 'contain', flexShrink: 0 }} />
          ) : (
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `linear-gradient(135deg, ${primary}, ${primary}88)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>📡</div>
          )}
          {sidebarOpen && domainConfig.logo_url && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#0f172a', fontWeight: '800', fontSize: '16px', letterSpacing: '-0.3px' }}>{domainConfig.app_name || 'FibraACS'}</div>
              <div style={{ color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '600' }}>Network Manager</div>
            </div>
          )}
        </div>

        {/* Toggle button */}
        <button onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{ position: 'absolute', top: '20px', right: '-12px', width: '24px', height: '24px', borderRadius: '50%', background: '#1e293b', border: '2px solid #0f172a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#64748b', zIndex: 10 }}>
          {sidebarOpen ? '◀' : '▶'}
        </button>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px', overflowY: 'auto', overflowX: 'hidden' }}>
          {sidebarOpen && <div style={{ fontSize: '10px', color: domainConfig.logo_url ? '#94a3b8' : '#ffffff66', textTransform: 'uppercase', letterSpacing: '2px', padding: '8px 8px 4px', marginBottom: '4px' }}>Navigation</div>}
          {menuItems.map(item => (
            <button key={item.id} onClick={() => setPage(item.id)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                padding: sidebarOpen ? '11px 12px' : '11px 0', justifyContent: sidebarOpen ? 'flex-start' : 'center',
                background: page === item.id ? `${primary}22` : 'transparent',
                border: page === item.id ? `1px solid ${primary}33` : '1px solid transparent',
                borderRadius: '10px', cursor: 'pointer', marginBottom: '4px',
                color: page === item.id ? primary : (domainConfig.logo_url ? '#374151' : '#64748b'),
                transition: 'all 0.15s', position: 'relative'
              }}
              onMouseEnter={e => { if (page !== item.id) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
              onMouseLeave={e => { if (page !== item.id) e.currentTarget.style.background = 'transparent'; }}>
              <span style={{ fontSize: '18px', flexShrink: 0 }}>{item.icon}</span>
              {sidebarOpen && <span style={{ fontSize: '13px', fontWeight: page === item.id ? '700' : '500', whiteSpace: 'nowrap', animation: 'slideIn 0.2s ease' }}>{item.label}</span>}
              {item.badge && (
                <span style={{ marginLeft: 'auto', background: '#ef4444', color: 'white', borderRadius: '10px', fontSize: '10px', fontWeight: '700', padding: '2px 6px', minWidth: '18px', textAlign: 'center' }}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>

        {/* User info */}
        <div style={{ padding: '12px 8px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: domainConfig.logo_url ? '#f8fafc' : 'rgba(255,255,255,0.04)', marginBottom: '8px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: `${primary}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', flexShrink: 0 }}>
              {user.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            {sidebarOpen && (
              <div style={{ overflow: 'hidden', animation: 'fadeIn 0.2s ease' }}>
                <div style={{ color: domainConfig.logo_url ? '#1e293b' : '#e2e8f0', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.name}</div>
                <div style={{ color: domainConfig.logo_url ? '#64748b' : '#ffffff77', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' }}>{user.role?.replace(/_/g, ' ')}</div>
              </div>
            )}
          </div>
          <button onClick={() => setShowChangePwd(true)}
            style={{ width: '100%', padding: '9px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', color: '#94a3b8', fontSize: '12px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginBottom: '6px' }}>
            <span>🔑</span>
            {sidebarOpen && <span>Change Password</span>}
          </button>
          <button onClick={logout}
            style={{ width: '100%', padding: '9px', background: domainConfig.logo_url ? '#fef2f2' : 'rgba(239,68,68,0.1)', border: domainConfig.logo_url ? '1px solid #fecaca' : '1px solid rgba(239,68,68,0.15)', borderRadius: '8px', color: '#ef4444', fontSize: '12px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            <span>⏻</span>
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePwd && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '400px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>🔑 Change Password</h3>
              <button onClick={() => { setShowChangePwd(false); setPwdMsg({ text: '', type: '' }); }} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px' }}>✕</button>
            </div>
            {pwdMsg.text && <div style={{ background: pwdMsg.type==='error'?'#fef2f2':'#ecfdf5', color: pwdMsg.type==='error'?'#dc2626':'#16a34a', padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', fontWeight: '500' }}>{pwdMsg.text}</div>}
            {['current:Current Password', 'newPwd:New Password', 'confirm:Confirm New Password'].map(f => {
              const [key, label] = f.split(':');
              return (
                <div key={key} style={{ marginBottom: '14px' }}>
                  <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>{label}</label>
                  <input type="password" style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', boxSizing: 'border-box', outline: 'none' }}
                    value={pwdForm[key]} onChange={e => setPwdForm({ ...pwdForm, [key]: e.target.value })}
                    onKeyPress={ev => ev.key==='Enter' && handleChangePwd()} />
                </div>
              );
            })}
            <button onClick={handleChangePwd} disabled={pwdLoading}
              style={{ width: '100%', padding: '12px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', marginTop: '6px' }}>
              {pwdLoading ? '⏳ Changing...' : '💾 Change Password'}
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div style={{ flex: 1, marginLeft: sidebarOpen ? '240px' : '68px', transition: 'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)', display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Topbar */}
        <div style={{ background: 'white', borderBottom: '1px solid #f1f5f9', padding: '0 28px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 1px 0 #f1f5f9' }}>
          <div>
            <h2 style={{ fontSize: '17px', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.3px' }}>
              {menuItems.find(m => m.id === page)?.label || 'Dashboard'}
            </h2>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
              {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {pendingCount > 0 && (
              <button onClick={() => setPage('devices')}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', color: '#92400e', fontWeight: '600' }}>
                <span style={{ animation: 'pulse 2s infinite' }}>⚡</span>
                {pendingCount} ONT{pendingCount > 1 ? 's' : ''} pending
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #f1f5f9' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', animation: 'pulse 2s infinite' }} />
              <span style={{ fontSize: '12px', color: '#475569', fontWeight: '500' }}>System Online</span>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div style={{ flex: 1, padding: '28px', animation: 'fadeIn 0.3s ease' }}>
          <PageComponent primary={primary} user={user} onNavigate={setPage} />
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 28px', borderTop: '1px solid #f1f5f9', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>© {new Date().getFullYear()} {domainConfig.app_name || 'FibraACS'} · All Rights Reserved</div>
          <div style={{ fontSize: '12px', color: '#94a3b8' }}>
            {domainConfig.tenant_code ? 
              <>Powered by <span style={{ color: primary, fontWeight: '600' }}>{domainConfig.app_name || domainConfig.tenant_code}</span></> : 
              <>Powered by <span style={{ color: primary, fontWeight: '600' }}>Fibralink Technologies</span></>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default App;
